import { useEffect, useState } from "react";
import { Check, X, Trash2, Plus, Ban, CalendarPlus, Send, Bell, CheckCircle2, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/AuthContext";
import PTComplete from "./PTComplete";
import AdminPTClients from "./AdminPTClients";
import AdminPTPayments from "./AdminPTPayments";
import AdminPTFeedback from "./AdminPTFeedback";
import AdminPTTrajects from "./AdminPTTrajects";
import AdminPTLinks from "./AdminPTLinks";
import { StatusChip, BOOKING_STATUS, CLIENT_STATUS, PAYMENT_STATUS, eur, ACCENT } from "./ptShared";

const WEEKDAYS = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
const SUBS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "calendar", label: "Calendar" },
  { key: "bookings", label: "Bookings" },
  { key: "trajects", label: "Trajects" },
  { key: "clients", label: "Clients" },
  { key: "packages", label: "Packages" },
  { key: "payments", label: "Payments" },
  { key: "feedback", label: "Feedback" },
  { key: "availability", label: "Availability" },
  { key: "links", label: "Booking Links" },
  { key: "settings", label: "Page Settings" },
];
const STATUS_COLORS = { pending: "#D4A93A", approved: "#4ADE80", declined: "#F87171", cancelled: "#F87171", completed: "#4ADE80", no_show: "#F87171" };

// Admin → Personal Training (centrale werkomgeving)
export default function AdminPT() {
  const [sub, setSub] = useState("dashboard");
  const [clientSel, setClientSel] = useState(null);
  const [clientTab, setClientTab] = useState(null);
  const [completeBooking, setCompleteBooking] = useState(null);
  const [payPrefill, setPayPrefill] = useState(null);
  const [rev, setRev] = useState(0); // forceert herladen van subtabs na acties

  const openClient = (email, tab = null) => { setClientSel(email); setClientTab(tab); setSub("clients"); };
  const openComplete = (booking) => setCompleteBooking(booking);
  const newPaymentFor = (email) => { setPayPrefill(email); setSub("payments"); };

  return (
    <div data-testid="admin-pt">
      <div className="flex flex-wrap gap-2 mb-8">
        {SUBS.map((s) => (
          <button key={s.key} onClick={() => setSub(s.key)}
            className={`chip px-4 py-2 border transition-colors ${sub === s.key ? "text-[#0A0A0A]" : "border-[#F5F3F0]/20 text-[#A3A3A3] hover:text-[#F5F3F0]"}`}
            style={sub === s.key ? { background: "var(--joh-accent)", borderColor: "var(--joh-accent)" } : {}}
            data-testid={`pt-sub-${s.key}`}>{s.label}</button>
        ))}
      </div>
      <div key={rev}>
        {sub === "dashboard" && <PTDashboard go={setSub} openClient={openClient} openComplete={openComplete} />}
        {sub === "calendar" && <PTCalendar />}
        {sub === "bookings" && <PTBookings onComplete={openComplete} />}
        {sub === "trajects" && <AdminPTTrajects openClient={openClient} />}
        {sub === "clients" && <AdminPTClients selectedEmail={clientSel} profileTab={clientTab} onClearSelection={setClientSel} onNewPayment={newPaymentFor} />}
        {sub === "packages" && <PTPackages />}
        {sub === "payments" && <AdminPTPayments prefillEmail={payPrefill} onPrefillUsed={() => setPayPrefill(null)} />}
        {sub === "feedback" && <AdminPTFeedback />}
        {sub === "availability" && <PTAvailability />}
        {sub === "links" && <AdminPTLinks />}
        {sub === "settings" && <PTSettings />}
      </div>
      {completeBooking && (
        <PTComplete booking={completeBooking} onClose={() => setCompleteBooking(null)} onDone={() => setRev((r) => r + 1)} />
      )}
    </div>
  );
}

/* ---------- Dashboard (werkdashboard: stats + acties) ---------- */
const ACTION_COLORS = { 1: "#D4A93A", 2: "#60A5FA", 3: "#A3A3A3" };

function PTDashboard({ go, openClient, openComplete }) {
  const [stats, setStats] = useState(null);
  const [actions, setActions] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [clients, setClients] = useState([]);
  const [newBooking, setNewBooking] = useState(false);
  const [q, setQ] = useState(""); // centrale klant-zoekbalk
  const [weekAvail, setWeekAvail] = useState({}); // {date: vrije slots} — weekdrukte
  const load = () => {
    api.get("/pt/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/pt/admin/actions").then(({ data }) => setActions(data.items)).catch(() => setActions([]));
    api.get("/pt/admin/bookings").then(({ data }) => setBookings(data.items || [])).catch(() => {});
    api.get("/pt/admin/clients").then(({ data }) => setClients(data.items || [])).catch(() => {});
    // vrije slots komende 7 dagen (voor de weekstrip)
    api.get("/pt/admin/calendar", { params: { days: 7 } }).then(({ data }) => {
      const m = {};
      (data.items || []).forEach((it) => { m[it.date] = (it.available || []).length; });
      setWeekAvail(m);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!stats || actions === null) return <div className="chip text-[#A3A3A3]">Laden…</div>;

  const bookingsById = {};
  bookings.forEach((b) => { bookingsById[b.id] = b; });
  const today = new Date().toISOString().slice(0, 10);
  const todaySessions = bookings.filter((b) => b.date === today && ["pending", "approved"].includes(b.status)).sort((a, b) => a.time.localeCompare(b.time));
  const upcomingSessions = bookings.filter((b) => b.date > today && b.status === "approved").sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)).slice(0, 6);
  const attentionClients = clients.filter((c) => c.onboarding_status === "intake_completed" || c.onboarding_status === "intake_required" || c.status === "new").slice(0, 8);

  // Centrale klant-zoekbalk (springt direct naar het klantprofiel)
  const searchResults = q.trim()
    ? clients.filter((c) => `${c.name || ""} ${c.first_name || ""} ${c.last_name || ""} ${c.email || ""} ${c.phone || ""}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6)
    : [];

  // Deze week (ma-zo): alle open sessies per dag
  const nowD = new Date();
  const monday = new Date(nowD); monday.setDate(nowD.getDate() - ((nowD.getDay() + 6) % 7));
  const weekDays = Array.from({ length: 7 }, (_, i) => { const dd = new Date(monday); dd.setDate(monday.getDate() + i); return dd.toISOString().slice(0, 10); });
  const weekSessions = bookings.filter((b) => weekDays.includes(b.date) && ["pending", "approved"].includes(b.status));

  const cards = [
    { label: "NIEUWE PT-AANMELDINGEN", value: stats.new_signups, testid: "pt-stat-new", accent: stats.new_signups > 0, go: "clients" },
    { label: "INTAKES AWAITING REVIEW", value: stats.intakes_awaiting_review, testid: "pt-stat-intakes", accent: stats.intakes_awaiting_review > 0, go: "clients" },
    { label: "ACTIEVE PT-KLANTEN", value: stats.active_clients, testid: "pt-stat-active", go: "clients" },
    { label: "SESSIES VANDAAG", value: stats.sessions_today, testid: "pt-stat-today", accent: stats.sessions_today > 0, go: "calendar" },
    { label: "AANKOMENDE SESSIES", value: stats.upcoming_sessions, testid: "pt-stat-upcoming", go: "calendar" },
    { label: "AFGERONDE SESSIES", value: stats.completed_sessions, testid: "pt-stat-done", go: "bookings" },
    { label: "OPENSTAANDE BETALINGEN", value: stats.open_payments, testid: "pt-stat-open-pay", accent: stats.open_payments > 0, go: "payments" },
    { label: "BETAALLINKS TE VERSTUREN", value: stats.links_ready, testid: "pt-stat-links", accent: stats.links_ready > 0, go: "payments" },
    { label: "HERINNERINGEN TE KEUREN", value: stats.reminders_queued, testid: "pt-stat-reminders", accent: stats.reminders_queued > 0, go: "payments" },
    { label: "FEEDBACK TE VERWERKEN", value: stats.feedback_new, testid: "pt-stat-feedback", accent: stats.feedback_new > 0, go: "feedback" },
    { label: "ZONDER VOLGENDE AFSPRAAK", value: stats.clients_no_next, testid: "pt-stat-nonext", accent: stats.clients_no_next > 0, go: "clients" },
    { label: "PAKKETTEN BIJNA OP", value: stats.packages_almost_done, testid: "pt-stat-pkglow", go: "packages" },
  ];

  const runAction = async (a) => {
    // Direct uitvoeren vanuit het overzicht waar mogelijk
    if (a.kind === "send_payment") {
      try {
        await api.post(`/pt/admin/payments/${a.payment_id}/send`);
        toast.success("Betaallink verstuurd");
        load();
      } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Versturen mislukt"); }
      return;
    }
    if (a.kind === "reminder") {
      try {
        await api.post(`/pt/admin/payments/${a.payment_id}/reminder/send`);
        toast.success("Herinnering verstuurd");
        load();
      } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Versturen mislukt"); }
      return;
    }
    if (a.booking_id && bookingsById[a.booking_id] && ["session_today", "complete_session", "trainer_feedback"].includes(a.kind)) {
      openComplete(bookingsById[a.booking_id]);
      return;
    }
    if (a.kind === "pending_booking") { go("bookings"); return; }
    if (a.kind === "client_feedback") { go("feedback"); return; }
    if (a.kind === "payment_open") { go("payments"); return; }
    if (a.kind === "intake_review" || a.kind === "intake_follow_up") { openClient(a.email, "Intake"); return; }
    if (["package_low", "package_expiring", "package_empty"].includes(a.kind)) { go("packages"); return; }
    openClient(a.email);
  };

  const actionBtn = (a) => {
    if (a.kind === "send_payment") return <><Send size={11}/> Verstuur nu</>;
    if (a.kind === "reminder") return <><Bell size={11}/> Verstuur herinnering</>;
    if (["session_today", "complete_session"].includes(a.kind)) return <><CheckCircle2 size={11}/> Afronden</>;
    if (a.kind === "trainer_feedback") return <><CheckCircle2 size={11}/> Feedback</>;
    if (a.kind === "intake_review") return <><CheckCircle2 size={11}/> Review intake</>;
    if (a.kind === "pending_booking") return <>Beoordeel</>;
    if (a.kind === "client_feedback") return <>Bekijk</>;
    return <>Open <ChevronRight size={11}/></>;
  };

  return (
    <div>
      {/* Centrale klant-zoekbalk */}
      <div className="relative mb-8 max-w-md" data-testid="pt-client-search">
        <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A4A4A]"/>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek klant op naam, e-mail of telefoon…"
          className="editorial-input dark pl-6 w-full" data-testid="pt-client-search-input"/>
        {searchResults.length > 0 && (
          <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-[#0A0A0A] border border-[#F5F3F0]/20" data-testid="pt-client-search-results">
            {searchResults.map((c) => (
              <button key={c.email} onClick={() => { setQ(""); openClient(c.email); }}
                className="w-full text-left px-4 py-3 hover:bg-[#F5F3F0]/5 border-b border-[#F5F3F0]/10 last:border-0 transition-colors"
                data-testid={`pt-client-search-${c.email}`}>
                <span className="font-display uppercase text-sm">{c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email}</span>
                <span className="chip text-[#4A4A4A] ml-2">{c.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6" data-testid="pt-stats">
        {cards.map((c) => (
          <button key={c.testid} onClick={() => go(c.go)} className="border border-[#F5F3F0]/15 p-4 text-left hover:border-[#F5F3F0]/40 transition-colors" data-testid={c.testid}>
            <div className="chip text-[#A3A3A3]">{c.label}</div>
            <div className="font-display text-4xl mt-2" style={c.accent ? { color: "var(--joh-accent)" } : {}}>{c.value}</div>
          </button>
        ))}
      </div>

      {/* Snelle acties */}
      <div className="flex flex-wrap gap-2 mb-10" data-testid="pt-quick-actions">
        <button onClick={() => setNewBooking(true)} className="chip px-4 py-2.5 border transition-colors" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid="pt-quick-booking">+ New Booking</button>
        <button onClick={() => go("clients")} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 text-[#DDD6CB]" data-testid="pt-quick-client">+ New Client</button>
        <button onClick={() => go("trajects")} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 text-[#DDD6CB]" data-testid="pt-quick-traject">+ New Traject</button>
        <button onClick={() => go("availability")} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 text-[#DDD6CB]" data-testid="pt-quick-availability">Manage Availability</button>
      </div>

      <div className="chip mb-3" style={{ color: "var(--joh-accent)" }} data-testid="pt-actions-title">— ACTION REQUIRED ({actions.length})</div>
      {actions.length === 0 ? (
        <div className="border border-[#F5F3F0]/15 p-6 chip text-[#4A4A4A] mb-10" data-testid="pt-actions-empty">Alles bijgewerkt — geen openstaande acties.</div>
      ) : (
        <div className="line-t line-dark mb-10" data-testid="pt-actions">
          {actions.map((a, i) => (
            <div key={`${a.kind}-${a.booking_id || a.payment_id || a.email}-${i}`} className="line-b line-dark py-3 flex flex-wrap items-center gap-3" data-testid={`pt-action-${i}`}>
              <span className="chip px-2 py-0.5 border shrink-0" style={{ color: ACTION_COLORS[a.urgency], borderColor: `${ACTION_COLORS[a.urgency]}55` }}>{a.urgency === 1 ? "URGENT" : a.urgency === 2 ? "BINNENKORT" : "OP MERKEN"}</span>
              <span className="chip text-[#4A4A4A] w-24 shrink-0">{a.date}</span>
              <span className="text-sm text-[#DDD6CB] flex-1 min-w-48">{a.title} — <span className="text-[#A3A3A3]">{a.client}</span></span>
              <button onClick={() => runAction(a)} className="h-8 px-3 border chip inline-flex items-center gap-1.5 transition-colors" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid={`pt-action-run-${i}`}>{actionBtn(a)}</button>
            </div>
          ))}
        </div>
      )}

      <div className="chip text-[#A3A3A3] mb-3">— DEZE WEEK ({weekSessions.length})</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-10" data-testid="pt-week">
        {weekDays.map((iso, i) => {
          const daySess = weekSessions.filter((b) => b.date === iso).sort((a, b) => a.time.localeCompare(b.time));
          const isToday = iso === today;
          return (
            <div key={iso} className={`border p-3 min-h-20 ${isToday ? "" : "border-[#F5F3F0]/10"}`} style={isToday ? { borderColor: "var(--joh-accent)" } : {}} data-testid={`pt-week-${iso}`}>
              <div className="chip" style={isToday ? { color: "var(--joh-accent)" } : { color: "#4A4A4A" }}>{["MA", "DI", "WO", "DO", "VR", "ZA", "ZO"][i]} {iso.slice(8, 10)}</div>
              {iso in weekAvail && (
                <div className={`chip mt-1 ${weekAvail[iso] > 0 ? "text-[#4ADE80]" : "text-[#F87171]"}`} data-testid={`pt-week-free-${iso}`}>
                  {weekAvail[iso] > 0 ? `${weekAvail[iso]} vrij` : "vol"}
                </div>
              )}
              {daySess.map((b) => (
                <button key={b.id} onClick={() => openClient(b.email)} className="chip text-[#DDD6CB] block mt-1.5 hover:underline underline-offset-2 text-left" data-testid={`pt-week-item-${b.id}`}>
                  {b.time} {b.first_name}
                </button>
              ))}
            </div>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="chip text-[#A3A3A3] mb-3">— TODAY ({todaySessions.length})</div>
          {todaySessions.length === 0 ? <div className="chip text-[#4A4A4A] py-2" data-testid="pt-today-empty">Geen PT-sessies vandaag.</div> : (
            <div className="line-t line-dark" data-testid="pt-today">
              {todaySessions.map((b) => (
                <div key={b.id} className="line-b line-dark py-3 flex flex-wrap items-center gap-3" data-testid={`pt-today-${b.id}`}>
                  <span className="chip text-[#DDD6CB] w-14">{b.time}</span>
                  <button onClick={() => openClient(b.email)} className="font-display uppercase flex-1 text-left hover:underline underline-offset-4">{b.first_name} {b.last_name}</button>
                  <StatusChip map={BOOKING_STATUS} value={b.status} />
                  <button onClick={() => openComplete(b)} className="h-8 px-3 border chip inline-flex items-center gap-1" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid={`pt-today-complete-${b.id}`}>Afronden</button>
                </div>
              ))}
            </div>
          )}
          <div className="chip text-[#A3A3A3] mt-8 mb-3">— UPCOMING</div>
          {upcomingSessions.length === 0 ? <div className="chip text-[#4A4A4A] py-2">Geen aankomende PT's.</div> : (
            <div className="line-t line-dark" data-testid="pt-upcoming">
              {upcomingSessions.map((b) => (
                <div key={b.id} className="line-b line-dark py-3 flex flex-wrap items-center gap-3">
                  <span className="chip text-[#DDD6CB] w-32">{b.date} · {b.time}</span>
                  <span className="font-display uppercase flex-1">{b.first_name} {b.last_name}</span>
                  {b.traject_id && <span className="chip px-2 py-0.5 border" style={{ borderColor: "#60A5FA55", color: "#60A5FA" }}>TRAJECT</span>}
                  <StatusChip map={BOOKING_STATUS} value={b.status} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="chip text-[#A3A3A3] mb-3">— CLIENTS DIE AANDACHT NODIG HEBBEN</div>
          {attentionClients.length === 0 ? <div className="chip text-[#4A4A4A] py-2" data-testid="pt-clients-ok">Alle klanten zijn bijgewerkt.</div> : (
            <div className="line-t line-dark" data-testid="pt-attention-clients">
              {attentionClients.map((c) => (
                <button key={c.email} onClick={() => openClient(c.email, c.onboarding_status === "intake_completed" ? "Intake" : null)} className="w-full text-left line-b line-dark py-3 flex flex-wrap items-center gap-3 hover:bg-[#F5F3F0]/5 px-2 -mx-2 transition-colors" data-testid={`pt-attention-${c.email}`}>
                  <span className="font-display uppercase flex-1">{c.name}</span>
                  <span className="chip px-2 py-0.5 border" style={{ color: c.onboarding_status === "intake_completed" ? "#F97316" : "#D4A93A", borderColor: "currentColor" }}>
                    {c.onboarding_status === "intake_completed" ? "AWAITING REVIEW" : c.onboarding_status === "intake_required" ? "INTAKE REQUIRED" : "NIEUWE AANMELDING"}
                  </span>
                  <span className="chip text-[#4A4A4A]">{(c.registered_at || "").slice(0, 10)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {newBooking && <NewBookingModal clients={clients} onClose={() => setNewBooking(false)} onDone={() => { setNewBooking(false); load(); }} />}
    </div>
  );
}

/* + New Booking — admin boeking, direct Confirmed (spec 50) */
function NewBookingModal({ clients, onClose, onDone }) {
  const [f, setF] = useState({ email: "", date: "", time: "18:00", note: "" });
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (f.email) api.get("/pt/admin/packages").then(({ data }) => setPackages(data.items.filter((p) => p.email === f.email && p.remaining > 0))).catch(() => {});
  }, [f.email]);
  const create = async () => {
    setBusy(true);
    try {
      await api.post("/pt/admin/bookings", { ...f, package_id: f.package_id || undefined });
      toast.success("Boeking aangemaakt — direct Confirmed, klant krijgt een bevestigingsmail");
      onDone();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Aanmaken mislukt"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="pt-newbooking-modal">
        <div className="chip text-[#A3A3A3] mb-1">// NEW BOOKING — ADMIN (direct Confirmed)</div>
        <div className="font-display uppercase text-2xl mb-5">Sessie inplannen.</div>
        <div className="space-y-4">
          <div><label className="chip text-[#A3A3A3]">Klant*</label>
            <select value={f.email} onChange={(e) => setF((p) => ({ ...p, email: e.target.value, package_id: "" }))} className="editorial-input dark" data-testid="pt-newbooking-client">
              <option value="" className="bg-[#0A0A0A]">Kies klant…</option>
              {clients.map((c) => <option key={c.email} value={c.email} className="bg-[#0A0A0A]">{c.name} — {c.email}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="chip text-[#A3A3A3]">Datum*</label><input type="date" value={f.date} onChange={(e) => setF((p) => ({ ...p, date: e.target.value }))} className="editorial-input dark" data-testid="pt-newbooking-date" /></div>
            <div><label className="chip text-[#A3A3A3]">Tijd*</label><input type="time" step="3600" value={f.time} onChange={(e) => setF((p) => ({ ...p, time: e.target.value }))} className="editorial-input dark" data-testid="pt-newbooking-time" /></div>
          </div>
          {packages.length > 0 && (
            <div><label className="chip text-[#A3A3A3]">Van pakket afschrijven (optioneel)</label>
              <select value={f.package_id || ""} onChange={(e) => setF((p) => ({ ...p, package_id: e.target.value }))} className="editorial-input dark" data-testid="pt-newbooking-package">
                <option value="" className="bg-[#0A0A0A]">Losse sessie</option>
                {packages.map((p) => <option key={p.id} value={p.id} className="bg-[#0A0A0A]">{p.label} — {p.remaining} resterend</option>)}
              </select>
            </div>
          )}
          <div><label className="chip text-[#A3A3A3]">Notitie</label><input value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))} className="editorial-input dark" data-testid="pt-newbooking-note" /></div>
          <button onClick={create} disabled={busy || !f.email || !f.date} className="w-full py-3.5 chip disabled:opacity-40" style={{ background: "var(--joh-accent)", color: "#0A0A0A" }} data-testid="pt-newbooking-submit">{busy ? "…" : "BOEKING AANMAKEN"}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Bookings ---------- */
function BookingRow({ b, onDone, onComplete }) {
  const today = new Date().toISOString().slice(0, 10);
  const act = async (action) => {
    try {
      await api.post(`/pt/admin/bookings/${b.id}/${action}`);
      toast.success(action === "approve" ? "Goedgekeurd — klant krijgt bevestigingsmail" : action === "decline" ? "Afgewezen — klant krijgt een e-mail" : action === "no-show" ? "Gemarkeerd als no-show" : "Geannuleerd");
      onDone();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Actie mislukt"); }
  };
  const st = BOOKING_STATUS[b.status] || { nl: b.status, color: "#A3A3A3" };
  return (
    <div className="line-b line-dark py-3.5 flex flex-wrap items-center gap-3 md:gap-4" data-testid={`pt-booking-${b.id}`}>
      <span className="chip text-[#DDD6CB] w-36 shrink-0">{b.date} · {b.time}</span>
      <span className="font-display text-lg uppercase flex-1 min-w-32 truncate">{b.first_name} {b.last_name}</span>
      <span className="chip text-[#A3A3A3] hidden md:inline">{b.email}</span>
      <span className="chip text-[#A3A3A3] w-20">{b.booking_type === "package" ? "PACKAGE" : "SINGLE"}</span>
      <span className="chip px-2 py-0.5 border" style={{ color: st.color, borderColor: `${st.color}55` }} data-testid={`pt-status-${b.id}`}>{st.nl.toUpperCase()}</span>
      <span className="flex gap-1 flex-wrap">
        {b.status === "pending" && (
          <>
            <button onClick={() => act("approve")} className="h-8 px-3 border border-[#4ADE80]/50 text-[#4ADE80] chip inline-flex items-center gap-1 hover:bg-[#4ADE80] hover:text-[#0A0A0A] transition-colors" data-testid={`pt-approve-${b.id}`}><Check size={12}/> Approve</button>
            <button onClick={() => act("decline")} className="h-8 px-3 border border-[#F87171]/50 text-[#F87171] chip inline-flex items-center gap-1 hover:bg-[#F87171] hover:text-[#0A0A0A] transition-colors" data-testid={`pt-decline-${b.id}`}><X size={12}/> Decline</button>
          </>
        )}
        {b.status === "approved" && b.date <= today && (
          <>
            <button onClick={() => onComplete(b)} className="h-8 px-3 border chip inline-flex items-center gap-1 transition-colors" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid={`pt-complete-${b.id}`}><CheckCircle2 size={12}/> Sessie afronden</button>
            <button onClick={() => { if (window.confirm("Markeren als no-show?")) act("no-show"); }} className="h-8 px-3 border border-[#F87171]/40 text-[#F87171] chip transition-colors" data-testid={`pt-noshow-${b.id}`}>No-show</button>
          </>
        )}
        {b.status === "completed" && (
          <button onClick={() => onComplete(b)} className="h-8 px-3 border border-[#F5F3F0]/25 text-[#DDD6CB] chip inline-flex items-center gap-1 transition-colors" data-testid={`pt-editfb-${b.id}`}>{b.feedback ? "Feedback bewerken" : "+ Feedback"}</button>
        )}
        {(b.status === "approved" || b.status === "pending") && (
          <button onClick={() => { if (window.confirm("Boeking annuleren?")) act("cancel"); }} className="h-8 px-3 border border-[#F5F3F0]/20 text-[#A3A3A3] chip hover:text-[#F87171] transition-colors" data-testid={`pt-cancel-${b.id}`}>Cancel</button>
        )}
      </span>
    </div>
  );
}

function PTBookings({ onComplete }) {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");
  const load = () => api.get("/pt/admin/bookings", { params: status ? { status } : {} }).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  return (
    <div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {["", "pending", "approved", "completed", "no_show", "declined", "cancelled"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`chip px-3 py-1.5 border transition-colors ${status === s ? "text-[#0A0A0A]" : "border-[#F5F3F0]/20 text-[#A3A3A3]"}`}
            style={status === s ? { background: "var(--joh-accent)", borderColor: "var(--joh-accent)" } : {}} data-testid={`pt-filter-${s || "all"}`}>{s ? (BOOKING_STATUS[s]?.nl || s).toUpperCase() : "ALLE"}</button>
        ))}
      </div>
      {items === null ? <div className="chip text-[#A3A3A3]">Laden…</div>
        : items.length === 0 ? <div className="border border-[#F5F3F0]/15 p-8 text-center chip text-[#4A4A4A]" data-testid="pt-bookings-empty">Geen boekingen{status ? ` met status ${BOOKING_STATUS[status]?.nl || status}` : ""}.</div>
        : <div className="line-t line-dark">{items.map((b) => <BookingRow key={b.id} b={b} onDone={load} onComplete={onComplete} />)}</div>}
    </div>
  );
}

/* ---------- Calendar (agenda) ---------- */
function PTCalendar() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/pt/admin/calendar", { params: { days: 21 } }).then(({ data }) => setItems(data.items)).catch(() => setItems([])); }, []);
  return (
    <div data-testid="pt-calendar">
      <div className="chip text-[#A3A3A3] mb-4">— CENTRALE PT-PLANNING: boekingen, aanvragen, trajectsessies, beschikbare en geblokkeerde momenten</div>
      {items === null ? <div className="chip text-[#A3A3A3]">Laden…</div>
        : items.map((day) => (
          <div key={day.date} className="mb-6" data-testid={`pt-cal-day-${day.date}`}>
            <div className="chip mb-2" style={{ color: day.date === new Date().toISOString().slice(0, 10) ? "var(--joh-accent)" : "#DDD6CB" }}>
              {WEEKDAYS[day.weekday]} {day.date}{day.date === new Date().toISOString().slice(0, 10) ? " — VANDAAG" : ""}
            </div>
            {day.bookings.length === 0 && day.available.length === 0 && day.blocked.length === 0 ? (
              <div className="chip text-[#4A4A4A] py-1">Geen planning.</div>
            ) : (
              <div className="line-t line-dark">
                {day.bookings.map((b) => (
                  <div key={b.id} className="line-b line-dark py-2.5 flex flex-wrap items-center gap-3" data-testid={`pt-cal-booking-${b.id}`}>
                    <span className="chip text-[#DDD6CB] w-14">{b.time}</span>
                    <span className="font-display text-lg uppercase flex-1 min-w-32">{b.first_name} {b.last_name}</span>
                    {b.traject_id && <span className="chip px-2 py-0.5 border" style={{ borderColor: "#60A5FA55", color: "#60A5FA" }}>TRAJECT</span>}
                    <span className="chip text-[#A3A3A3]">{b.booking_type === "package" ? "PACKAGE" : "SINGLE"}</span>
                    <StatusChip map={BOOKING_STATUS} value={b.status} />
                  </div>
                ))}
                {day.available.map((t) => (
                  <div key={t} className="line-b line-dark py-2 flex items-center gap-3 opacity-60">
                    <span className="chip text-[#A3A3A3] w-14">{t}</span>
                    <span className="chip text-[#4A4A4A] flex-1">BESCHIKBAAR</span>
                  </div>
                ))}
                {day.blocked.map((e, i) => (
                  <div key={i} className="line-b line-dark py-2 flex items-center gap-3">
                    <span className="chip text-[#F87171] w-14">{e.slot_time || "hele dag"}</span>
                    <span className="chip text-[#F87171] flex-1">GEBLOKKEERD{e.reason ? ` — ${e.reason}` : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

/* ---------- Availability (1-uurs tijdsblokken + Google Calendar) ---------- */
const HOURS = Array.from({ length: 17 }, (_, i) => `${String(i + 6).padStart(2, "0")}:00`); // 06:00 t/m 22:00

function PTAvailability() {
  const [days, setDays] = useState(null);
  const [exceptions, setExceptions] = useState(null);
  const [exc, setExc] = useState({ kind: "block", date: "", slot: "" });
  const [gcal, setGcal] = useState(null);

  const load = () => {
    api.get("/pt/admin/availability").then(({ data }) => setDays(data.days)).catch(() => {});
    api.get("/pt/admin/exceptions").then(({ data }) => setExceptions(data.items)).catch(() => {});
    api.get("/pt/admin/gcal/status").then(({ data }) => setGcal(data)).catch(() => setGcal({ configured: false, connected: false }));
  };
  useEffect(load, []);

  const toggleHour = async (wd, hour) => {
    const cur = days[String(wd)] || [];
    const next = cur.includes(hour) ? cur.filter((h) => h !== hour) : [...cur, hour].sort();
    setDays((d) => ({ ...d, [wd]: next }));
    try {
      await api.put(`/pt/admin/availability/${wd}`, { hours: next });
      toast.success(`${WEEKDAYS[wd]} bijgewerkt`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Opslaan mislukt");
      load();
    }
  };

  const addExc = async () => {
    if (!exc.date) { toast.error("Kies een datum"); return; }
    try {
      await api.post("/pt/admin/exceptions", { ...exc, slot: exc.slot || undefined });
      toast.success(exc.kind === "block" ? "Geblokkeerd — niet boekbaar voor klanten" : "Extra tijdslot toegevoegd");
      setExc({ kind: "block", date: "", slot: "" });
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Opslaan mislukt"); }
  };
  const delExc = async (id) => { await api.delete(`/pt/admin/exceptions/${id}`); toast.success("Verwijderd"); load(); };

  const connectGcal = async () => {
    try {
      const { data } = await api.get("/pt/admin/gcal/connect");
      window.location.href = data.authorization_url;
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Koppelen mislukt"); }
  };
  const disconnectGcal = async () => {
    if (!window.confirm("Google Calendar-koppeling verwijderen?")) return;
    await api.post("/pt/admin/gcal/disconnect");
    toast.success("Koppeling verwijderd");
    load();
  };

  return (
    <div data-testid="pt-availability">
      {/* Terugkerende uurblokken per dag */}
      <div className="chip text-[#A3A3A3] mb-2">— TERUGKERENDE BESCHIKBAARHEID — 1-UURS BLOKKEN (KLIK OM AAN/UIT TE ZETTEN)</div>
      <p className="chip text-[#4A4A4A] mb-4">18:00 betekent automatisch 18:00–19:00. Klanten zien alleen aangezette blokken.</p>
      {days === null ? <div className="chip text-[#A3A3A3]">Laden…</div> : (
        <div className="line-t line-dark mb-10" data-testid="pt-avail-days">
          {WEEKDAYS.map((w, wd) => {
            const active = days[String(wd)] || [];
            return (
              <div key={wd} className="line-b line-dark py-3.5" data-testid={`pt-avail-day-${wd}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-lg uppercase w-32 shrink-0">{w}</span>
                  {HOURS.map((h) => {
                    const on = active.includes(h);
                    return (
                      <button key={h} onClick={() => toggleHour(wd, h)}
                        className="chip px-2.5 py-1.5 border transition-colors"
                        style={on ? { background: "var(--joh-accent)", color: "#0A0A0A", borderColor: "var(--joh-accent)" } : { borderColor: "#F5F3F022", color: "#4A4A4A" }}
                        title={on ? `${h}–${String(parseInt(h) + 1).padStart(2, "0")}:00 beschikbaar — klik om te blokkeren` : `${h}–${String(parseInt(h) + 1).padStart(2, "0")}:00 niet beschikbaar`}
                        data-testid={`pt-hour-${wd}-${h}`}>
                        {h}
                      </button>
                    );
                  })}
                  <span className="chip text-[#4A4A4A] ml-auto">{active.length} blokken</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Uitzonderingen */}
        <div>
          <div className="chip text-[#A3A3A3] mb-4">— UITZONDERINGEN & EXTRA SLOTS</div>
          <div className="border border-[#F5F3F0]/15 p-5 mb-4">
            <div className="grid grid-cols-3 gap-3 items-end">
              <div><label className="chip text-[#A3A3A3]">Type</label>
                <select value={exc.kind} onChange={(e) => setExc((f) => ({ ...f, kind: e.target.value }))} className="editorial-input dark" data-testid="pt-exc-kind">
                  <option value="block" className="bg-[#0A0A0A]">Blokkeren</option>
                  <option value="extra" className="bg-[#0A0A0A]">Extra slot</option>
                </select>
              </div>
              <div><label className="chip text-[#A3A3A3]">Datum</label><input type="date" value={exc.date} onChange={(e) => setExc((f) => ({ ...f, date: e.target.value }))} className="editorial-input dark" data-testid="pt-exc-date" /></div>
              <div><label className="chip text-[#A3A3A3]">Tijd {exc.kind === "block" ? "(leeg = hele dag)" : "*"}</label><input type="time" step="3600" value={exc.slot} onChange={(e) => setExc((f) => ({ ...f, slot: e.target.value }))} className="editorial-input dark" data-testid="pt-exc-slot" /></div>
            </div>
            <button onClick={addExc} className="chip mt-4 px-4 py-2.5 border inline-flex items-center gap-2 transition-colors" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid="pt-exc-add">
              {exc.kind === "block" ? <><Ban size={13}/> Blokkeren</> : <><CalendarPlus size={13}/> Extra slot toevoegen</>}
            </button>
          </div>
          {exceptions === null ? <div className="chip text-[#A3A3A3]">Laden…</div>
            : exceptions.length === 0 ? <div className="border border-[#F5F3F0]/15 p-6 chip text-[#4A4A4A]" data-testid="pt-exc-empty">Geen uitzonderingen — bv. vakantie of ziekte blokkeer je hier.</div>
            : <div className="line-t line-dark">{exceptions.map((x) => (
                <div key={x.id} className="line-b line-dark py-3 flex items-center gap-4" data-testid={`pt-exc-${x.id}`}>
                  <span className="chip px-2 py-0.5 border" style={x.kind === "block" ? { color: "#F87171", borderColor: "#F8717155" } : { color: "#4ADE80", borderColor: "#4ADE8055" }}>{x.kind === "block" ? "BLOK" : "EXTRA"}</span>
                  <span className="text-sm text-[#DDD6CB] flex-1">{x.date}{x.slot ? ` · ${x.slot}–${String(parseInt(x.slot) + 1).padStart(2, "0")}:00` : " · hele dag"}</span>
                  <button onClick={() => delExc(x.id)} className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-red-400" data-testid={`pt-exc-del-${x.id}`}><Trash2 size={13}/></button>
                </div>
              ))}</div>}
        </div>

        {/* Google Calendar koppeling */}
        <div>
          <div className="chip text-[#A3A3A3] mb-4">— PERSOONLIJKE AGENDA (GOOGLE CALENDAR)</div>
          <div className="border border-[#F5F3F0]/15 p-5" data-testid="pt-gcal-card">
            <p className="text-sm text-[#A3A3A3] mb-4">
              Koppel je persoonlijke agenda: afspraken daarin blokkeren automatisch overlappende PT-tijdsblokken op de website, en nieuwe PT-boekingen verschijnen automatisch in je agenda.
            </p>
            {gcal === null ? <div className="chip text-[#A3A3A3]">Laden…</div>
              : !gcal.configured ? (
                <div className="chip text-[#4A4A4A]" data-testid="pt-gcal-not-configured">
                  Nog niet geconfigureerd — hiervoor moeten Google-credentials (Client ID/Secret) aan de app worden toegevoegd. Vraag dit aan de ontwikkelaar.
                </div>
              ) : gcal.connected ? (
                <div className="flex flex-wrap items-center gap-4">
                  <span className="chip" style={{ color: "#4ADE80" }} data-testid="pt-gcal-connected">✓ GEKOPPELD — {gcal.email}</span>
                  <button onClick={disconnectGcal} className="chip px-3 py-2 border border-[#F87171]/40 text-[#F87171]" data-testid="pt-gcal-disconnect">Ontkoppel</button>
                </div>
              ) : (
                <button onClick={connectGcal} className="chip px-4 py-2.5 border transition-colors" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid="pt-gcal-connect">
                  Koppel Google Calendar
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Packages ---------- */
function PTPackages() {
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => {
    api.get("/pt/admin/packages").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
    api.get("/pt/settings").then(({ data }) => setSettings(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const adj = async (p, delta) => {
    try { await api.put(`/pt/admin/packages/${p.id}`, { remaining: Math.max(0, p.remaining + delta) }); load(); }
    catch { toast.error("Aanpassen mislukt"); }
  };
  const renew = async (p) => {
    try {
      await api.post(`/pt/admin/packages/${p.id}/renew`, {});
      toast.success("Betaalverzoek klaargezet — verstuur via de Payments-tab");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Vernieuwen mislukt"); }
  };
  const setPkg = (i, k, v) => setSettings((s) => ({ ...s, packages: s.packages.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const saveDefs = async () => {
    setSaving(true);
    try {
      await api.put("/pt/admin/settings", { packages: settings.packages });
      toast.success("Pakket-definities opgeslagen");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Opslaan mislukt"); }
    setSaving(false);
  };
  return (
    <div data-testid="pt-packages">
      {/* Pakket-definities (spec 51) */}
      {settings && (
        <div className="mb-10" data-testid="pt-package-defs">
          <div className="chip text-[#A3A3A3] mb-3">— PAKKET-DEFINITIES (naam, sessies, prijs, geldigheid, featured, actief, volgorde — max 2 featured openbaar)</div>
          <div className="space-y-3">
            {settings.packages.map((p, i) => (
              <div key={p.key} className={`border p-4 ${p.active ? "border-[#F5F3F0]/15" : "border-[#F5F3F0]/8 opacity-50"}`} data-testid={`pt-def-${p.key}`}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><label className="chip text-[#A3A3A3]">Naam (NL)</label><input value={p.name} onChange={(e) => setPkg(i, "name", e.target.value)} className="editorial-input dark" data-testid={`pt-def-name-${p.key}`} /></div>
                  <div><label className="chip text-[#A3A3A3]">Sessies</label><input type="number" min="1" value={p.sessions} onChange={(e) => setPkg(i, "sessions", parseInt(e.target.value) || 1)} className="editorial-input dark" /></div>
                  <div><label className="chip text-[#A3A3A3]">Prijs (€)</label><input type="number" min="0" step="0.01" value={p.price} onChange={(e) => setPkg(i, "price", parseFloat(e.target.value) || 0)} className="editorial-input dark" data-testid={`pt-def-price-${p.key}`} /></div>
                  <div><label className="chip text-[#A3A3A3]">Geldig (dagen)</label><input type="number" min="1" value={p.validity_days || 31} onChange={(e) => setPkg(i, "validity_days", parseInt(e.target.value) || 31)} className="editorial-input dark" /></div>
                </div>
                <div className="mt-3"><label className="chip text-[#A3A3A3]">Omschrijving</label><input value={p.description || ""} onChange={(e) => setPkg(i, "description", e.target.value)} className="editorial-input dark" /></div>
                <div className="flex flex-wrap gap-5 mt-3 items-center">
                  <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!p.featured} onChange={(e) => setPkg(i, "featured", e.target.checked)} className="w-4 h-4 accent-[#D4A93A]" data-testid={`pt-def-featured-${p.key}`} /><span className="chip text-[#DDD6CB]">Featured</span></label>
                  <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={p.active !== false} onChange={(e) => setPkg(i, "active", e.target.checked)} className="w-4 h-4 accent-[#D4A93A]" data-testid={`pt-def-active-${p.key}`} /><span className="chip text-[#DDD6CB]">Actief</span></label>
                  <div className="flex items-center gap-2"><span className="chip text-[#A3A3A3]">Volgorde</span><input type="number" value={p.order ?? i} onChange={(e) => setPkg(i, "order", parseInt(e.target.value) || 0)} className="editorial-input dark w-20" /></div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={saveDefs} disabled={saving} className="chip mt-4 px-5 py-3" style={{ background: "var(--joh-accent)", color: "#0A0A0A" }} data-testid="pt-defs-save">{saving ? "…" : "DEFINITIES OPSLAAN"}</button>
        </div>
      )}

      {/* Uitgegeven pakketten met balans (spec 52) */}
      <div className="chip text-[#A3A3A3] mb-3">— UITGEGEVEN PAKKETTEN</div>
      <div className="hidden md:grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-4 chip text-[#4A4A4A] mb-1 px-0">
        <span>KLANT</span><span>TOTAL</span><span>SCHEDULED</span><span>COMPLETED</span><span>REMAINING</span><span></span>
      </div>
      {items === null ? <div className="chip text-[#A3A3A3]">Laden…</div>
        : items.length === 0 ? <div className="border border-[#F5F3F0]/15 p-8 text-center chip text-[#4A4A4A]" data-testid="pt-packages-empty">Nog geen PT-pakketten verkocht.</div>
        : <div className="line-t line-dark">{items.map((p) => {
            const expired = p.valid_until < new Date().toISOString();
            return (
              <div key={p.id} className="line-b line-dark py-3.5 flex flex-wrap items-center gap-4" data-testid={`pt-package-${p.id}`}>
                <span className="font-display text-lg uppercase flex-1 min-w-32 truncate">{p.name || p.email}</span>
                <span className="chip text-[#A3A3A3] hidden md:inline">{p.email}</span>
                <span className="chip text-[#DDD6CB] w-14 text-center">{p.total}</span>
                <span className="chip text-[#60A5FA] w-14 text-center" data-testid={`pt-pkg-scheduled-${p.id}`}>{p.scheduled ?? 0}</span>
                <span className="chip text-[#4ADE80] w-14 text-center" data-testid={`pt-pkg-completed-${p.id}`}>{p.completed ?? 0}</span>
                <span className="chip w-16 text-center" style={{ color: "var(--joh-accent)" }} data-testid={`pt-pkg-remaining-${p.id}`}>{p.remaining}</span>
                <span className="chip text-[#4A4A4A]">{expired ? "VERLOPEN" : `t/m ${p.valid_until.slice(0, 10)}`}</span>
                <span className="flex gap-1">
                  <button onClick={() => adj(p, -1)} disabled={p.remaining <= 0} className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-[#F5F3F0] disabled:opacity-30" data-testid={`pt-pkg-min-${p.id}`}>−</button>
                  <button onClick={() => adj(p, 1)} disabled={p.remaining >= p.total} className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-[#F5F3F0] disabled:opacity-30" data-testid={`pt-pkg-plus-${p.id}`}>+</button>
                </span>
                <button onClick={() => renew(p)} className="chip px-2.5 py-1.5 border border-[#F5F3F0]/20 text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors" title="Nieuw betaalverzoek voor hetzelfde pakket" data-testid={`pt-pkg-renew-${p.id}`}>Vernieuw</button>
              </div>
            );
          })}</div>}
      <p className="chip text-[#4A4A4A] mt-3">Een Pending booking telt niet als gebruikte training; verplaatsen veroorzaakt geen dubbele afschrijving.</p>
    </div>
  );
}

/* ---------- Page Settings ---------- */
function PTSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { api.get("/pt/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="chip text-[#A3A3A3]">Laden…</div>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/pt/admin/settings", s);
      toast.success("PT-instellingen opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
    setBusy(false);
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media/upload", fd);
      set("hero_video", data.url);
      toast.success("Video geüpload — vergeet niet op te slaan");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Upload mislukt"); }
    setUploading(false);
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="pt-settings">
      <div className="chip text-[#4A4A4A]">Pakketten, prijzen en featured-instellingen beheer je nu in de tab Packages.</div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="chip text-[#A3A3A3]">Sessieduur (min)</label><input type="number" min="15" value={s.session_minutes} onChange={(e) => set("session_minutes", e.target.value)} className="editorial-input dark" /></div>
        <div><label className="chip text-[#A3A3A3]">Tussenruimte slots (min)</label><input type="number" min="15" value={s.slot_interval} onChange={(e) => set("slot_interval", e.target.value)} className="editorial-input dark" /></div>
      </div>
      <div className="chip text-[#4A4A4A]">— Planningsregels</div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="chip text-[#A3A3A3]">Buffer tussen sessies (min)</label><input type="number" min="0" value={s.buffer_minutes ?? 30} onChange={(e) => set("buffer_minutes", e.target.value)} className="editorial-input dark" data-testid="pt-set-buffer" /></div>
        <div><label className="chip text-[#A3A3A3]">Min. aanmeldtijd (uur)</label><input type="number" min="0" value={s.min_notice_hours ?? 4} onChange={(e) => set("min_notice_hours", e.target.value)} className="editorial-input dark" data-testid="pt-set-notice" /></div>
        <div><label className="chip text-[#A3A3A3]">Boekingshorizon (dagen vooruit)</label><input type="number" min="1" value={s.horizon_days ?? 60} onChange={(e) => set("horizon_days", e.target.value)} className="editorial-input dark" data-testid="pt-set-horizon" /></div>
        <div><label className="chip text-[#A3A3A3]">Klant annuleren tot (uur vooraf)</label><input type="number" min="0" value={s.cancel_hours ?? 24} onChange={(e) => set("cancel_hours", e.target.value)} className="editorial-input dark" data-testid="pt-set-cancel" /></div>
      </div>
      <div>
        <label className="chip text-[#A3A3A3]">Hero-video /pt (url)</label>
        <input value={s.hero_video} onChange={(e) => set("hero_video", e.target.value)} className="editorial-input dark" data-testid="pt-set-video" />
        <div className="mt-2 flex items-center gap-3">
          <label className="chip px-3 py-2 border border-[#F5F3F0]/25 cursor-pointer inline-flex items-center gap-2 hover:text-[#F5F3F0] text-[#A3A3A3]" data-testid="pt-set-video-upload">
            {uploading ? "Uploaden…" : "Upload nieuwe video"}
            <input type="file" accept="video/*" className="hidden" onChange={uploadVideo} disabled={uploading} />
          </label>
          {s.hero_video && <a href={s.hero_video} target="_blank" rel="noreferrer" className="chip text-[#4A4A4A] underline underline-offset-4">bekijk huidige</a>}
        </div>
      </div>
      <div><label className="chip text-[#A3A3A3]">Locatie titel</label><input value={s.location_name} onChange={(e) => set("location_name", e.target.value)} className="editorial-input dark" data-testid="pt-set-locname" /></div>
      <div><label className="chip text-[#A3A3A3]">Adres (klikbaar → Google Maps)</label><input value={s.location_address || ""} onChange={(e) => set("location_address", e.target.value)} className="editorial-input dark" data-testid="pt-set-address" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="chip text-[#A3A3A3]">Telefoon (klikbaar)</label><input value={s.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="editorial-input dark" data-testid="pt-set-phone" /></div>
        <div><label className="chip text-[#A3A3A3]">E-mail (klikbaar)</label><input value={s.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="editorial-input dark" data-testid="pt-set-email" /></div>
      </div>
      <div><label className="chip text-[#A3A3A3]">Locatie tekst</label><textarea rows={3} value={s.location_text} onChange={(e) => set("location_text", e.target.value)} className="editorial-input dark w-full" data-testid="pt-set-loctext" /></div>
      <button onClick={save} disabled={busy} className="px-8 py-3.5 chip" style={{ background: "var(--joh-accent)", color: "#0A0A0A" }} data-testid="pt-set-save">{busy ? "Opslaan…" : "Opslaan"}</button>
    </div>
  );
}
